import { useEffect, useState } from "react";
import { ArrowRight, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { INTENTS } from "@/lib/intents";
import { publicApi, errMsg } from "@/lib/api";
import { useVisitor } from "@/lib/visitor";

const IntentOption = ({ item, index, onPick }) => {
  const Icon = item.icon;
  return (
    <motion.button initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.06 }}
      onClick={() => onPick(item.id)} data-testid={`intent-option-${item.id}`}
      className="group flex w-full items-center gap-4 rounded-2xl border border-slate-200 bg-white p-4 text-left transition-[border-color,background-color,transform] duration-200 hover:-translate-y-0.5 hover:border-[#1D64B4] hover:bg-[#F8FAFC]">
      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl" style={{ background: `${item.color}14`, color: item.color }}><Icon className="h-5 w-5" /></span>
      <span className="flex-1">
        <span className="block font-display text-base font-semibold text-slate-900">{item.label}</span>
        <span className="block text-sm text-slate-500">{item.sub}</span>
      </span>
      <ArrowRight className="h-4 w-4 shrink-0 text-slate-300 transition-transform group-hover:translate-x-1 group-hover:text-[#1D64B4]" />
    </motion.button>
  );
};

const ContactFields = ({ form, setForm }) => (
  <div className="rounded-2xl bg-[#F8FAFC] p-4" data-testid="questionnaire-contact">
    <p className="text-sm font-medium text-slate-700">May we know you a little? <span className="font-normal text-slate-400">(optional)</span></p>
    <div className="mt-3 grid gap-2 sm:grid-cols-2">
      <Input placeholder="Nickname" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white" data-testid="questionnaire-nickname-input" />
      <Input type="tel" inputMode="tel" placeholder="Mobile number" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-white" data-testid="questionnaire-mobile-input" />
    </div>
    <p className="mt-2 text-xs text-slate-400">We'll only use this to follow up with you personally.</p>
  </div>
);

export const ServePrompt = ({ open, onOpenChange }) => {
  const { profile, setProfile, setIntent } = useVisitor();
  const [form, setForm] = useState({ name: "", phone: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({ name: profile?.nickname || "", phone: profile?.mobile || "" });
  }, [open, profile]);

  const pick = async (id) => {
    const name = form.name.trim();
    const phone = form.phone.trim();
    if ((name || phone) && (!name || phone.length < 7)) {
      toast.error("Please share both a nickname and a mobile number, or leave both empty.");
      return;
    }
    setSaving(true);
    try {
      const body = { intent: id, ...(name ? { name, phone } : {}) };
      const { data } = profile?.id
        ? await publicApi.patch(`/visitors/${profile.id}/profile`, body)
        : await publicApi.post("/visitors", body);
      const nickname = name || profile?.nickname || "";
      setProfile({ ...(profile || {}), id: data.id, nickname, mobile: phone || profile?.mobile || "" });
      setIntent(id);
      onOpenChange(false);
      toast.success(nickname ? `Thank you, ${nickname}! We've prepared a few next steps for you.` : "We've prepared a few next steps for you.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto rounded-3xl border-0 bg-white p-0 sm:max-w-xl" data-testid="intent-questionnaire-modal">
        <div className="bg-gradient-to-r from-[#0A3A6B] to-[#1D64B4] px-7 py-6 text-white">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-200">Welcome to Victory Parañaque</p>
          <DialogTitle className="mt-2 font-display text-2xl font-bold tracking-tight">How do you want us to serve you today?</DialogTitle>
          <DialogDescription className="mt-1 text-sm text-sky-100/90">Choose what fits you best, and we'll gently guide you from here.</DialogDescription>
        </div>
        <div className="space-y-4 p-6 sm:p-7">
          <ContactFields form={form} setForm={setForm} />
          <div className={`space-y-2.5 ${saving ? "pointer-events-none opacity-60" : ""}`}>
            {INTENTS.map((it, i) => <IntentOption key={it.id} item={it} index={i} onPick={pick} />)}
          </div>
          <button onClick={() => onOpenChange(false)} className="flex w-full items-center justify-center gap-1.5 pt-1 text-sm font-medium text-slate-500 transition-colors hover:text-[#0A3A6B]" data-testid="questionnaire-skip-button">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />} Skip for now and explore the site
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
